import { AxisBottom, AxisLeft } from '@visx/axis';
import {
    Chart,
    Wrapper as DefaultWrapper,
} from 'components/DataVis/TimeSeriesChart';
import {
    formatSeriesData,
    getPointerMoveHandler,
    yAxisTickFormat,
} from '../MultiPredictionTimeSeriesChart/utils';
import { scaleLinear, scaleTime } from 'd3-scale';
import { GlyphCircle } from '@visx/glyph';
import { ParentSize } from '@visx/responsive';
import { PlaceholderShimmerBackground } from 'utils/fragments';
import PredictionLineSeries from '../MultiPredictionTimeSeriesChart/components/PredictionLineSeries';
import TickLabel from '../MultiPredictionTimeSeriesChart/components/TickLabel';
import Tooltip from '../MultiPredictionTimeSeriesChart/components/Tooltip';
import dayjs from 'dayjs';
import { getXAxisTickFns } from '../TikTokSearchTimeSeries/utils';
import styled from 'styled-components';
import { useTooltip } from '@visx/tooltip';
import { voronoi } from '@visx/voronoi';

const X_AXIS_HEIGHT = 50;
const Y_AXIS_WIDTH = 70;
const STROKE_WIDTH = 3;

const formatAndParseTime = (v) => {
    const s = dayjs(v, 'YYYY-MM-DD').format('MMM D, YYYY');
    return s;
};

const Placeholder = styled.div.attrs(({ shimmerWidth = '10000px' }) => ({
    shimmerWidth,
}))`
    grid-area: chart;
    min-height: ${({ height }) => `${height}px`};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius_sm};
    ${({ isLoading }) => (isLoading ? PlaceholderShimmerBackground : null)}
`;

function MultiTimeSeriesChart({ data, width, height }) {
    const { xAxisTickFormat, xAxisTickValues } = getXAxisTickFns(data);
    const {
        showTooltip,
        hideTooltip,
        tooltipData,
        tooltipLeft,
        tooltipTop,
        tooltipOpen,
    } = useTooltip({
        tooltipOpen: false,
        tooltipLeft: 0,
        tooltipTop: 0,
        tooltipData: null,
    });
    const { accessors, formattedData, xMin, xMax, yMin, yMax } =
        formatSeriesData(data);
    const xScale = scaleTime()
        .domain([xMin, xMax])
        .range([Y_AXIS_WIDTH + STROKE_WIDTH, width - STROKE_WIDTH - 24]);
    const yScale = scaleLinear()
        .domain([yMin, yMax])
        .range([height - X_AXIS_HEIGHT - STROKE_WIDTH, 24 + STROKE_WIDTH]);
    const x = (d) => xScale(dayjs(accessors.xAccessor(d)).toDate());
    const y = (d) => yScale(accessors.yAccessor(d));

    const voronoiInstance = voronoi({ x, y, width, height });
    const dataVoronoi = formattedData.map(({ historical, prediction }) =>
        voronoiInstance(
            historical.concat(prediction).filter((row) => {
                const date = dayjs(accessors.xAccessor(row)).toDate();
                return date >= xMin && date <= xMax;
            }),
        ),
    );

    return (
        <div css="position: relative;">
            <svg width={width} height={height}>
                <defs>
                    <clipPath id="date-range-clip-hashtag">
                        <rect
                            x={Y_AXIS_WIDTH}
                            y={0}
                            width={Math.max(width - Y_AXIS_WIDTH - 24, 0)}
                            height={Math.max(height - X_AXIS_HEIGHT, 0)}
                            fill="transparent"
                        />
                    </clipPath>
                </defs>
                <AxisLeft
                    left={Y_AXIS_WIDTH}
                    scale={yScale}
                    orientation="left"
                    tickFormat={yAxisTickFormat}
                    tickComponent={TickLabel}
                    numTicks={3}
                    hideZero={true}
                    hideAxisLine={true}
                    hideTicks={true}
                />
                <AxisBottom
                    orientation="bottom"
                    top={height - X_AXIS_HEIGHT}
                    scale={xScale}
                    tickValues={xAxisTickValues.range(xMin, xMax, 1)}
                    tickFormat={xAxisTickFormat}
                    tickComponent={TickLabel}
                    hideAxisLine={true}
                    hideTicks={true}
                />
                <g clipPath="url(#date-range-clip-hashtag)">
                    {formattedData.map(
                        ({ id, historical, prediction, color }) => (
                            <PredictionLineSeries
                                key={id}
                                x={x}
                                y={y}
                                historical={historical}
                                prediction={prediction}
                                strokeColor={color}
                            />
                        ),
                    )}
                </g>
                {tooltipOpen ? (
                    <GlyphCircle
                        left={tooltipData.x}
                        top={tooltipData.y}
                        size={200}
                        css={`
                            fill: ${tooltipData.strokeColor};
                            stroke-width: 0;
                        `}
                    />
                ) : null}
                <rect
                    x={Y_AXIS_WIDTH}
                    y={0}
                    width={Math.max(width - Y_AXIS_WIDTH - 24, 0)}
                    height={Math.max(height - X_AXIS_HEIGHT, 0)}
                    fill="transparent"
                    onPointerMove={getPointerMoveHandler({
                        data: formattedData,
                        dataVoronoi,
                        tooltipData,
                        showTooltip,
                        x,
                        y,
                    })}
                    onPointerOut={() => hideTooltip()}
                    onPointerUp={() => hideTooltip()}
                />
            </svg>
            <Tooltip
                left={tooltipLeft}
                top={tooltipTop}
                chartWidth={width}
                chartHeight={height}
                accessors={[
                    'xAccessor',
                    'yAccessor',
                    'predictionAccessor',
                ].reduce((acc, k) => {
                    acc[k] = (d) => accessors[k](d);
                    return acc;
                }, {})}
                tooltipData={tooltipData}
                tooltipOpen={tooltipOpen}
                formatAndParseTime={formatAndParseTime}
                metricLabel={data?.[0]?.metricLabel}
            />
        </div>
    );
}

function MultiTimeSeriesChartOverview({
    children,
    data,
    chartHeight,
    isLoading,
    showPlaceholder,
    placeholderMessage,
    Wrapper = DefaultWrapper,
    wrapperRef,
    ...props
}) {
    return (
        <Wrapper ref={wrapperRef}>
            {children}
            {showPlaceholder ? (
                <Placeholder height={chartHeight + 40} isLoading={isLoading}>
                    {placeholderMessage}
                </Placeholder>
            ) : (
                <div>
                    <Chart as="div" height={chartHeight}>
                        <ParentSize>
                            {({ width, height }) => (
                                <MultiTimeSeriesChart
                                    data={data.filter(
                                        ({ timeSeries }) =>
                                            timeSeries && timeSeries.length > 0,
                                    )}
                                    width={width}
                                    height={height}
                                    {...props}
                                />
                            )}
                        </ParentSize>
                    </Chart>
                </div>
            )}
        </Wrapper>
    );
}

export default MultiTimeSeriesChartOverview;
